import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { flagUrl } from '../i18n/translations';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  LayoutDashboard,
  FileText,
  Bell,
  Users,
  BarChart3,
  Menu,
  Moon,
  Sun,
  LogOut,
  User,
  Settings,
  Key,
  Home,
  Palette,
  Globe,
  LifeBuoy,
  Mail,
  Camera,
  Package,
  BookOpen,
  Shield,
  ScanLine,
  HardDrive,
  Target,
  ClipboardList,
  CalendarClock,
  FileKey,
  HelpCircle,
} from 'lucide-react';
import api from '../utils/api';

// Küçük dil seçici dropdown (dashboard için)
const LangSwitcher = () => {
  const { lang, setLang, LANGUAGES } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <Button variant="ghost" size="sm" className="rounded-full relative gap-1.5 px-2.5" onClick={() => setOpen(v => !v)} title="Dil Seç / Language">
        <img
          src={flagUrl(LANGUAGES[lang]?.countryCode)}
          alt={LANGUAGES[lang]?.nativeName}
          className="w-5 h-auto rounded-sm"
          onError={e => { e.target.style.display = 'none'; }}
        />
        <span className="text-xs font-medium hidden sm:inline">{LANGUAGES[lang]?.nativeName}</span>
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 rounded-xl overflow-hidden shadow-xl z-50 min-w-[170px] border bg-background">
          {Object.entries(LANGUAGES).map(([code, info]) => (
            <button
              key={code}
              onClick={() => { setLang(code); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors text-left hover:bg-muted ${lang === code ? 'text-primary font-semibold bg-primary/5' : 'text-foreground'}`}
            >
              <img
                src={flagUrl(info.countryCode)}
                alt={info.nativeName}
                className="w-5 h-auto rounded-sm"
                onError={e => { e.target.style.display = 'none'; }}
              />
              <span>{info.nativeName}</span>
              {lang === code && <span className="ml-auto text-primary">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DashboardLayout = () => {
  const { user, logout, isSuperAdmin, isStoreAdmin, isConsultant, hasPermission } = useAuth();
  const { theme, toggleTheme, setThemeForContext } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadContacts, setUnreadContacts] = useState(0);
  const [unreadTickets, setUnreadTickets] = useState(0);

  useEffect(() => {
    setThemeForContext('dashboard');
    api.get('/site-settings').then(r => setSiteSettings(r.data)).catch(() => {});
    loadUnreadCounts();
    const interval = setInterval(loadUnreadCounts, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  const loadUnreadCounts = async () => {
    try {
      const res = await api.get('/notifications');
      setUnreadCount((res.data || []).filter(n => !n.is_read).length);
    } catch {}
    if (isSuperAdmin) {
      try {
        const res = await api.get('/admin/contact-requests');
        setUnreadContacts((res.data || []).filter(r => !r.is_read).length);
      } catch {}
      try {
        const res = await api.get('/admin/tickets');
        setUnreadTickets((res.data || []).filter(t => t.status === 'open' || t.status === 'pending').length);
      } catch {}
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const sideNavigation = React.useMemo(() => {
    const nav = [];

    if (isSuperAdmin) {
      nav.push(
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Raporlar', href: '/reports', icon: FileText },
        { name: 'İlanlar', href: '/listings', icon: Home },
        { name: 'Yeni Rapor', href: '/reports/new', icon: FileText },
        { name: 'Satış Hatırlatıcıları', href: '/sale-reminders', icon: CalendarClock },
        { name: 'Kira Sözleşmeleri', href: '/rental-contracts', icon: FileKey },
        { name: 'Kullanıcılar', href: '/admin/users', icon: Users },
        { name: 'Mağazalar', href: '/admin/stores', icon: Home },
        { name: 'API Anahtarları', href: '/admin/api-keys', icon: Key },
        { name: 'İstatistikler', href: '/admin/stats', icon: BarChart3 },
        { name: 'Disk & Depolama', href: '/admin/disk', icon: HardDrive },
        { name: 'Site İçeriği', href: '/admin/landing-content', icon: Globe },
        { name: 'Site Ayarları', href: '/admin/site-settings', icon: Settings },
        { name: 'Paketler', href: '/admin/packages', icon: Package },
        { name: 'Blog', href: '/admin/blog', icon: BookOpen },
        { name: 'İletişim Mesajları', href: '/admin/contact', icon: Mail },
        { name: 'Destek Talepleri', href: '/admin/tickets', icon: LifeBuoy },
        { name: 'Yasal Sayfalar', href: '/admin/legal', icon: Shield }
      );
    } else if (isStoreAdmin) {
      nav.push(
        { name: 'Dashboard', href: '/store/stats', icon: LayoutDashboard },
        { name: 'Raporlar', href: '/store/reports', icon: FileText },
        { name: 'Yeni Rapor', href: '/reports/new', icon: FileText },
        { name: 'İlanlar', href: '/listings', icon: Home },
        { name: 'Satış Hatırlatıcıları', href: '/sale-reminders', icon: CalendarClock },
        { name: 'Kira Sözleşmeleri', href: '/rental-contracts', icon: FileKey },
        { name: 'Danışmanlar', href: '/store/consultants', icon: Users },
        { name: 'Hedefler', href: '/store/goals', icon: Target },
        { name: 'Aylık Beyanlar', href: '/store/declarations', icon: ClipboardList },
        { name: 'Destek', href: '/support', icon: LifeBuoy }
      );
    } else if (isConsultant) {
      nav.push(
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Raporlarım', href: '/my-reports', icon: FileText },
        { name: 'Yeni Rapor', href: '/reports/new', icon: FileText },
        { name: 'İlanlarım', href: '/listings', icon: Home },
        { name: 'Satış Hatırlatıcıları', href: '/sale-reminders', icon: CalendarClock },
        { name: 'Kira Sözleşmeleri', href: '/rental-contracts', icon: FileKey },
        { name: 'Hedeflerim', href: '/goals', icon: Target },
        { name: 'Aylık Beyanım', href: '/monthly-declaration', icon: ClipboardList },
        { name: 'Destek', href: '/support', icon: LifeBuoy }
      );
    } else {
      nav.push(
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Raporlarım', href: '/my-reports', icon: FileText },
        { name: 'Yeni Rapor', href: '/reports/new', icon: FileText },
        { name: 'Satış Hatırlatıcıları', href: '/sale-reminders', icon: CalendarClock },
        { name: 'Kira Sözleşmeleri', href: '/rental-contracts', icon: FileKey },
        { name: 'Hedeflerim', href: '/goals', icon: Target },
        { name: 'Aylık Beyanım', href: '/monthly-declaration', icon: ClipboardList },
        { name: 'Destek', href: '/support', icon: LifeBuoy }
      );
    }

    if (hasPermission('image_editor') || siteSettings?.image_editor_global) {
      nav.push({ name: 'Görsel Düzenle', href: '/image-editor', icon: ScanLine });
    }

    nav.push({ name: 'Nasıl Kullanılır?', href: '/how-to-use', icon: HelpCircle });

    return nav;
  }, [isSuperAdmin, isStoreAdmin, isConsultant, hasPermission, siteSettings]);

  const isActive = (path) => location.pathname === path;
  const logoSrc = (theme === 'dark' ? siteSettings?.dashboard_logo_dark_url : siteSettings?.dashboard_logo_light_url) || siteSettings?.logo_url;

  const UserMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
          <Avatar className="ring-2 ring-primary/40 ring-offset-1">
            <AvatarImage src={user?.profile_image} alt={user?.full_name} />
            <AvatarFallback>{user?.full_name?.charAt(0)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user?.full_name}</p>
            <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
            <p className="text-xs leading-none text-muted-foreground capitalize mt-1">
              <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full">{user?.role}</span>
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile" className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            <span>Profil</span>
          </Link>
        </DropdownMenuItem>
        {isSuperAdmin && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/admin/site-settings" className="cursor-pointer">
                <Palette className="mr-2 h-4 w-4" />
                <span>Site Ayarları</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Çıkış Yap</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const SidebarContent = ({ onClose }) => (
    <>
      <div className="h-16 flex items-center px-4 border-b shrink-0">
        <Link to="/dashboard" className="flex items-center gap-2" onClick={onClose}>
          {logoSrc ? (
            <img src={logoSrc} alt="Logo" className="h-9 object-contain max-w-[140px]" />
          ) : (
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary-foreground" />
            </div>
          )}
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {sideNavigation.map((item) => {
          const isContact = item.href === '/admin/contact';
          const isTicket  = item.href === '/admin/tickets';
          const badge = isContact ? unreadContacts : isTicket ? unreadTickets : 0;
          return (
            <Link key={item.name} to={item.href} onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}>
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1">{item.name}</span>
              {badge > 0 && (
                <span className="ml-auto h-5 min-w-[1.25rem] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      <div className="border-t p-3 flex items-center gap-2 shrink-0">
        <Avatar className="h-8 w-8 ring-2 ring-primary/40 ring-offset-1">
          <AvatarImage src={user?.profile_image} alt={user?.full_name} />
          <AvatarFallback className="text-xs">{user?.full_name?.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{user?.full_name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sol Sidebar — masaüstü */}
      <aside className="fixed left-0 top-0 h-full w-60 bg-background border-r flex flex-col z-40 hidden md:flex">
        <SidebarContent onClose={() => {}} />
      </aside>

      {/* Sağ İçerik */}
      <div className="md:ml-60 flex-1 flex flex-col min-h-screen">
        {/* Üst Bar */}
        <header className="sticky top-0 z-30 h-16 border-b bg-background/95 backdrop-blur flex items-center justify-between px-4 gap-2">
          {/* Mobil hamburger */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 flex flex-col">
              <SidebarContent onClose={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="flex-1" />

          {/* Bildirimler + İstatistikler */}
          <nav className="flex items-center gap-1 mr-2">
            <Link to="/notifications">
              <Button variant={isActive('/notifications') ? 'default' : 'ghost'} size="sm" className="gap-2 relative">
                <span className="relative">
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 h-4 min-w-[1rem] px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </span>
                <span className="hidden md:inline">Bildirimler</span>
              </Button>
            </Link>
            {!isSuperAdmin && (
              <Link to="/my-stats">
                <Button variant={isActive('/my-stats') ? 'default' : 'ghost'} size="sm" className="gap-2">
                  <BarChart3 className="h-4 w-4" />
                  <span className="hidden md:inline">İstatistiklerim</span>
                </Button>
              </Link>
            )}
          </nav>

          <LangSwitcher />
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
            {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </Button>
          <UserMenu />
        </header>

        <main className="flex-1 container mx-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
